import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import baseURL from '../../config';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, IconButton, Box, Modal, Button, TextField, InputAdornment, CircularProgress, TablePagination, TableFooter } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import bgImage from '../../assets/shipimage.jpg';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/system';

// Styled Components for Modern UI

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  padding: theme.spacing(2),
  borderBottom: '1px solid #f0f0f0',
  backgroundColor: "red",
  color: '#fff',
  textTransform: 'uppercase',
  letterSpacing: '1px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f9f9f9',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
  },
}));

const VesselList = () => {
  const { id: vesselownerId } = useParams();

  const [shipDetails, setShipDetails] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0); // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(5); // Number of rows per page

  useEffect(() => {
    const fetchVesselList = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${baseURL}/api/vesselOwner/getshipdetailsbyvesselownerId/${vesselownerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setShipDetails(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching vessel list:', err);
        setLoading(false);
      }
    };

    fetchVesselList();
  }, [vesselownerId]);

  const handleOpenModal = (ship) => {
    if (ship) {
      const documents = Object.entries(ship)
        .filter(([key, value]) =>
          ["pipolicydocument", "maritimelabourconvention", "fsdocument", "dmlcp1", "dmlc2"].includes(key) &&
          value
        )
        .map(([key, value]) => {
          let filePath = typeof value === "string" ? value : value?.filePath;
          if (!filePath) return null;

          filePath = filePath.replace(/\\/g, "/");

          if (!filePath.startsWith("http")) {
            if (filePath.startsWith("/Documents")) {
              filePath = `${baseURL}${filePath}`;
            } else {
              filePath = `${baseURL}/${filePath}`;
            }
          }

          return {
            name: key,
            fileName: key,
            filePath: filePath,
          };
        })
        .filter(Boolean);

      setSelectedDocuments(documents);
      setOpenModal(true);
    }
  };

const handleSearch = () => {
  const lowercasedQuery = searchQuery.toLowerCase();

  return shipDetails.filter((ship) => {
    return (
      (ship.vesselname && ship.vesselname.toLowerCase().includes(lowercasedQuery)) ||
      (ship.IMO_Number && ship.IMO_Number.toLowerCase().includes(lowercasedQuery)) ||
      (ship.company_name?.company_shortname &&
        ship.company_name.company_shortname.toLowerCase().includes(lowercasedQuery))
    );
  });
};


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const ExpiryDate = ({ label, date }) => (
    <Typography variant="body1">
      <strong>{label}:</strong> {date ? new Date(date).toLocaleDateString("en-GB") : "-"}
    </Typography>
  );

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;

  return (
    <Box
      sx={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 6,
        px: 2,
      }}
    >
      {/* Search Bar */}
      <SearchContainer>
        <TextField
          label="Search Vessels"
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by vessel name, IMO, or owner"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 600,
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
            },
          }}
        />
      </SearchContainer>

      <TableContainer component={Paper} sx={{ marginTop: 3  }}>
        <Table>
          <TableHead >
            <TableRow>
              <StyledTableCell>Sr. No.</StyledTableCell>
              <StyledTableCell>Ship Information</StyledTableCell>
              <StyledTableCell>Insurance Details</StyledTableCell>
              <StyledTableCell>Certificates</StyledTableCell>
              <StyledTableCell>Documents</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {handleSearch()
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((ship, index) => (
                <StyledTableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography><strong>Vessel Name:</strong> {ship.vesselname}</Typography>
                    <Typography><strong>IMO Number:</strong> {ship.IMO_Number}</Typography>
                    <Typography><strong>Ship Type:</strong> {ship.vesseltype}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography><strong>P&I Number:</strong> {ship.pipolicynumber}</Typography>
                    <ExpiryDate label="Policy Validity" date={ship.policydateofv} />
                  </TableCell>
                  <TableCell>
                    <Typography><strong>MLC Cert No.:</strong> {ship.mlcCert}</Typography>
                    <ExpiryDate label="MLC Exp. Date" date={ship.mlcExpiryDate} />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenModal(ship)}>
                      <PictureAsPdfIcon sx={{ color: "red" }} />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))}
          </TableBody>

          {/* Table Footer with Pagination */}
          <TableFooter>
            <TableRow>
              <TableCell colSpan={5}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={handleSearch().length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                />
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      {/* Modal for Document Display */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "70%",
          bgcolor: "white",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          overflowY: "auto",
        }}>
          <Typography variant="h6" gutterBottom align="center">Vessel Documents</Typography>
          {selectedDocuments.length > 0 ? (
            selectedDocuments.map((doc, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: 2, borderRadius: 2 }}>
                <Box display="flex" alignItems="center">
                  <PictureAsPdfIcon sx={{ color: "red", mr: 1 }} />
                  <Typography variant="body1">{doc.fileName}</Typography>
                </Box>
                <Button href={doc.filePath} target="_blank" variant="contained" color="primary" size="small">Download</Button>
              </Paper>
            ))
          ) : (
            <Typography align="center" color="textSecondary">No documents available</Typography>
          )}
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="contained" color="error" onClick={() => setOpenModal(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default VesselList;
